import { ethers } from 'ethers';
import { getCurrentNetwork, switchToNetwork, isValidNetwork, getNetworkByChainId } from '../../config/blockchain';
import { cancelAllApiRequests } from '../../utils/ApiManager';
import { balanceManager } from './balanceManager';
import { registrationManager } from './registrationManager';

/**
 * Wallet Connection Manager for handling wallet connections and disconnections
 */
export class WalletConnectionManager {
  constructor() {
    this.isConnecting = false;
  }

  /**
   * Connect to wallet using AppKit (with modal)
   * This is for manual connection - always opens the WalletConnect modal
   */
  async connectWalletConnect(appKit, setters, debouncedCheckUserRegistration, shouldUseWalletCalls) {
    try {
      
      if (!appKit) {
        // If AppKit is not available, try MetaMask as fallback
        const hasMetaMask = typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask;
        if (hasMetaMask) {
          try {
            const result = await this.connectToMetaMask(setters, debouncedCheckUserRegistration, false, shouldUseWalletCalls);
            if (result.success) {
              return result;
            }
          } catch (error) {
            // MetaMask failed, continue to error message
          }
        }
        return { 
          success: false, 
          error: 'Wallet connection not available. Please configure WalletConnect project ID or install MetaMask.' 
        };
      }

      // Prevent multiple simultaneous connection attempts
      if (this.isConnecting) {
        return { success: false, message: 'Connection already in progress' };
      }

      this.isConnecting = true;

      try {
        await appKit.open();
      } catch (error) {
        this.isConnecting = false;
        return { success: false, error: error?.message || 'Failed to open wallet connection modal' };
      }
      
      // Clear manual disconnect flag since user is connecting
      try {
        localStorage.removeItem('wallet_manually_disconnected');
        localStorage.setItem('wallet_auto_connect', 'true');
      } catch (error) {
      }
      
      this.isConnecting = false;
      return { success: true, type: 'appkit' };
    } catch (error) {
      this.isConnecting = false;
      return { success: false, error: error.message || 'Connection failed' };
    }
  }

  /**
   * Auto-connect to wallet (without modal)
   * MetaMask connection is optional - only attempt if user has explicitly connected before
   */
  async autoConnectWallet(appKit, setters, debouncedCheckUserRegistration, shouldUseWalletCalls) {
    try {
      
      if (!appKit) {
        return;
      }

      // Prevent multiple simultaneous connection attempts
      if (this.isConnecting) {
        return;
      }

      this.isConnecting = true;
      
      // Check if user has explicitly enabled auto-connect
      const hasStoredConnection = localStorage.getItem('wallet_auto_connect') === 'true';
      const wasManuallyDisconnected = localStorage.getItem('wallet_manually_disconnected') === 'true';
      
      // Only attempt auto-connect if user has explicitly enabled it and hasn't manually disconnected
      if (!hasStoredConnection || wasManuallyDisconnected) {
        this.isConnecting = false;
        return;
      }
      
      // Check if MetaMask is available and has a selected address
      const hasMetaMask = typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask;
      const hasSelectedAddress = window.ethereum?.selectedAddress;
      
      // Only attempt MetaMask connection if available and has address
      // Fail silently if connection fails - MetaMask is optional
      if (hasMetaMask && hasSelectedAddress) {
        try {
          const result = await this.connectToMetaMask(setters, debouncedCheckUserRegistration, true, shouldUseWalletCalls);
          // Silently ignore failed connections - MetaMask is optional
          if (!result.success) {
            // Connection failed, but that's okay - MetaMask is optional
          }
        } catch (error) {
          // Ignora tutti gli errori di auto-connect silenziosamente
          // MetaMask è opzionale, quindi non è un problema se la connessione fallisce
        }
      }
    } catch (error) {
      // Ignora tutti gli errori - MetaMask è opzionale
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Connect directly to MetaMask
   */
  async connectToMetaMask(setters, debouncedCheckUserRegistration, isAutoConnect = false, shouldUseWalletCalls) {
    try {
      // Verifica che MetaMask sia disponibile
      if (typeof window.ethereum === 'undefined' || !window.ethereum.isMetaMask) {
        return { success: false, message: 'MetaMask is not installed' };
      }

      const { 
        setAccount, setProvider, setSigner, setIsConnected, setProviderType,
        setChainId, setIsCorrectNetwork, setCurrentNetwork, setBalance, setUsdtBalance,
        setIsUserRegistered, setRegistrationChecked, setUsername
      } = setters;

      // Get accounts (use different method for auto-connect vs manual connect)
      let accounts;
      try {
        // Add timeout to prevent hanging
        const requestPromise = window.ethereum.request({
          method: isAutoConnect ? 'eth_accounts' : 'eth_requestAccounts',
        });
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Connection timeout')), 10000)
        );
        
        accounts = await Promise.race([requestPromise, timeoutPromise]);
      } catch (requestError) {
        // Gestisci TUTTI gli errori silenziosamente - MetaMask è opzionale
        const errorCode = requestError?.code || requestError?.error?.code;
        const errorMessage = requestError?.message || requestError?.error?.message || String(requestError) || 'Unknown error';
        
        // Per auto-connect, fallisci sempre silenziosamente
        if (isAutoConnect) {
          return { success: false, message: 'Auto-connect failed silently' };
        }
        
        // Per connessioni manuali, ritorna un messaggio ma non lanciare errori
        if (errorCode === 4001 || 
            errorMessage.includes('User rejected') || 
            errorMessage.includes('rejected') ||
            errorMessage.includes('Failed to connect') ||
            errorMessage.includes('not authorized') ||
            errorMessage.includes('unauthorized')) {
          return { success: false, message: 'User rejected the connection request' };
        }
        
        // Per timeout o altri errori, ritorna silenziosamente
        return { success: false, message: 'Connection failed' };
      }
      
      if (!accounts || accounts.length === 0) {
        return { success: false, message: 'No accounts found' };
      }
      
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const account = accounts[0];
      
      setAccount(account);
      setProvider(provider);
      setSigner(signer);
      setIsConnected(true);
      setProviderType('metamask-direct');
      
      // Get network info
      const chainId = await provider.getNetwork().then(n => Number(n.chainId));
      setChainId(chainId);
      
      const networkValid = isValidNetwork(chainId);
      setIsCorrectNetwork(networkValid);
      
      if (networkValid) {
        const networkInfo = getNetworkByChainId(chainId);
        setCurrentNetwork(networkInfo);
        
        // Get balances
        provider.getBalance(account).then(balance => {
          setBalance(ethers.formatEther(balance));
        }).catch(() => {});
        
        // Use timing fix for USDT balance
        
        const canUseWalletCalls = typeof shouldUseWalletCalls === 'function' ? shouldUseWalletCalls() : true;
        if (canUseWalletCalls) {
          balanceManager.getUSDTBalanceWithParams(account, provider, networkInfo).then(usdtBal => {
            setUsdtBalance(usdtBal);
          }).catch(() => {});
        } else {
          setUsdtBalance('0');
        }
      }

      // Set up event listeners
      this.setupMetaMaskEventListeners(setters, debouncedCheckUserRegistration, shouldUseWalletCalls);

      // Clear manual disconnect flag since user is connecting
      try {
        localStorage.removeItem('wallet_manually_disconnected');
      } catch (error) {
      }

      // Check if user is registered after successful wallet connection
      const canUseWalletCalls = typeof shouldUseWalletCalls === 'function' ? shouldUseWalletCalls() : true;
      if (canUseWalletCalls) {
        debouncedCheckUserRegistration(account);
      } else if (setIsUserRegistered && setRegistrationChecked && setUsername) {
        setIsUserRegistered(false);
        setRegistrationChecked(true);
        setUsername(null);
      }
      
      return { success: true, account, networkValid, type: 'metamask-direct' };
    } catch (error) {
      // Gestisci TUTTI gli errori silenziosamente - MetaMask è opzionale
      // Non lanciare errori runtime, ritorna sempre un risultato
      const errorMessage = error?.message || String(error) || 'Unknown error';
      
      // Per auto-connect, fallisci sempre silenziosamente senza log
      if (isAutoConnect) {
        return { success: false, message: 'Auto-connect failed silently' };
      }
      
      // Per connessioni manuali, ritorna un messaggio ma non loggare
      return { success: false, message: 'Connection failed' };
    }
  }

  /**
   * Setup MetaMask event listeners
   */
  setupMetaMaskEventListeners(setters, debouncedCheckUserRegistration, shouldUseWalletCalls) {
    try {
      // Verifica che MetaMask sia ancora disponibile
      if (typeof window.ethereum === 'undefined' || !window.ethereum.isMetaMask) {
        return;
      }

      const { 
        setAccount,
        setChainId, setIsCorrectNetwork, setCurrentNetwork, setBalance, setUsdtBalance,
        setIsUserRegistered, setRegistrationChecked, setUsername
      } = setters;

      window.ethereum.on('accountsChanged', async (accounts) => {
        if (accounts.length === 0) {
          await this.disconnectWallet(setters, false);
        } else if (accounts[0] !== setters.account) {
          
          // Cancel all pending API requests immediately
          cancelAllApiRequests();
          
          // Reset registration state immediately for new account
          registrationManager.resetRegistrationState(setIsUserRegistered, setRegistrationChecked, setUsername);
          
          // Update account
          setAccount(accounts[0]);
          
          // Re-check user registration for new account (debounced)
          try {
            const canUseWalletCalls = typeof shouldUseWalletCalls === 'function' ? shouldUseWalletCalls() : true;
            if (canUseWalletCalls) {
              debouncedCheckUserRegistration(accounts[0]);
            } else {
              setIsUserRegistered(false);
              setRegistrationChecked(true);
              setUsername(null);
            }
            
            // Update balances for new account
            if (setters.currentNetwork && setters.provider) {
              const balance = await setters.provider.getBalance(accounts[0]);
              setBalance(ethers.formatEther(balance));
              
              if (canUseWalletCalls) {
                const usdtBal = await balanceManager.getUSDTBalance(accounts[0], setters.provider, setters.currentNetwork, setters.usdtBalance);
                setUsdtBalance(usdtBal);
              } else {
                setUsdtBalance('0');
              }
            }
          } catch (error) {
            // Ignora errori silenziosamente
          }
        }
      });

      window.ethereum.on('chainChanged', async (newChainId) => {
        const chainIdNumber = parseInt(newChainId, 16);
        setChainId(chainIdNumber);
        
        const networkValid = isValidNetwork(chainIdNumber);
        setIsCorrectNetwork(networkValid);
        
        if (networkValid) {
          const networkInfo = getNetworkByChainId(newChainId);
          setCurrentNetwork(networkInfo);
          
          // Refresh balances on new network
          if (setters.account && setters.provider) {
            setters.provider.getBalance(setters.account).then(balance => {
              setBalance(ethers.formatEther(balance));
            }).catch(() => {});
            
            const canUseWalletCalls = typeof shouldUseWalletCalls === 'function' ? shouldUseWalletCalls() : true;
            if (canUseWalletCalls) {
              balanceManager.getUSDTBalance(setters.account, setters.provider, networkInfo, setters.usdtBalance).then(usdtBal => {
                setUsdtBalance(usdtBal);
              }).catch(() => {});
            } else {
              setUsdtBalance('0');
            }
          }
        }
      });

      window.ethereum.on('disconnect', (error) => {
        this.disconnectWallet(setters, false);
      });
    } catch (error) {
      // Ignora errori nella configurazione degli event listener
      // MetaMask è opzionale, quindi non è un problema se la configurazione fallisce
    }
  }

  /**
   * Disconnect wallet
   */
  async disconnectWallet(setters, silent = false) {
    const {
      setAccount, setProvider, setSigner, setIsConnected, setProviderType,
      setChainId, setIsCorrectNetwork, setCurrentNetwork, setBalance, setUsdtBalance,
      setIsUserRegistered, setRegistrationChecked, setUsername, providerType, appKit
    } = setters;

    if (!silent) {
    }
    
    // Cancel ALL pending API requests immediately
    cancelAllApiRequests();
    balanceManager.cleanup();
    registrationManager.cleanup();
    
    if (!silent) {
    }
    
    try {
      // Handle different provider types with more thorough cleanup
      if (providerType === 'metamask-direct') {
        
        try {
          // Remove all possible MetaMask event listeners
          if (typeof window.ethereum !== 'undefined') {
            const events = ['accountsChanged', 'chainChanged', 'connect', 'disconnect', 'message'];
            events.forEach(event => {
              try {
                window.ethereum.removeAllListeners(event);
              } catch (e) {
                // Ignore individual listener removal errors
              }
            });
          }
        } catch (error) {
        }
        
      } else if (appKit) {
        
        try {
          // AppKit disconnect
          await appKit.disconnect();
        } catch (appKitError) {
        }
      }
      
      // Enhanced localStorage cleanup
      try {
        const keysToRemove = [
          'walletconnect',
          'WALLETCONNECT_DEEPLINK_CHOICE', 
          'WCM_MODAL_CLOSED_ON_CONNECT',
          'wc@2:client:0.3//session',
          'wc@2:core:0.3//keychain',
          'wc@2:core:0.3//messages',
          'wc@2:ethereum_provider:/chainId',
          'wc@2:ethereum_provider:/accounts'
        ];
        
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            // Ignore individual removal errors
          }
        });
        
        // Set manual disconnect flag only if not silent
        if (!silent) {
          localStorage.setItem('wallet_manually_disconnected', 'true');
          localStorage.removeItem('wallet_auto_connect');
        }
        
      } catch (error) {
      }
      
    } catch (error) {
    }

    // Reset all states regardless of any errors above
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setIsConnected(false);
    setChainId(null);
    setBalance('0');
    setUsdtBalance('0');
    setCurrentNetwork(null);
    setIsCorrectNetwork(false);
    setIsUserRegistered(null);
    setRegistrationChecked(false);
    setUsername(null);
    setProviderType(null);
    
    if (!silent) {
    }
  }

  /**
   * Switch to BSC network
   * MetaMask is optional - fails silently if not available
   */
  async switchToBSC(currentNetwork, isCorrectNetwork) {
    try {
      
      if (!currentNetwork || !isCorrectNetwork) {
        const targetNetwork = getCurrentNetwork();
        const networkKey = targetNetwork.chainId === 56 ? 'bscMainnet' : 'bscTestnet';
        
        const switched = await switchToNetwork(networkKey);
        if (switched) {
          return { success: true };
        } else {
          // MetaMask is optional - fail silently
          return { success: false, error: 'MetaMask not available or network switch failed' };
        }
      } else {
        return { success: true, message: 'Already on correct network' };
      }
    } catch (error) {
      // MetaMask is optional - fail silently
      return { success: false, error: 'Network switch failed' };
    }
  }

  /**
   * Ensure signer is available
   */
  async ensureSigner(signer, appKit, setProvider, setSigner) {
    try {
      
      if (signer) {
        return signer;
      }
      
      if (!appKit) {
        throw new Error('AppKit not initialized');
      }
      
      // Try to get signer from AppKit
      const ethersProvider = appKit.getWalletProvider();
      if (ethersProvider) {
        const newProvider = new ethers.BrowserProvider(ethersProvider);
        const newSigner = await newProvider.getSigner();
        
        // Update context state
        if (!setProvider.provider) setProvider(newProvider);
        setSigner(newSigner);
        
        return newSigner;
      }
      
      throw new Error('No wallet provider available');
    } catch (error) {
      throw error;
    }
  }
}

// Create a singleton instance
export const walletConnectionManager = new WalletConnectionManager(); 